import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useProjectStore from '../../store/projectStore';
import useUIStore from '../../store/uiStore';
import { getEmployees } from '../../services/employeeService';
import * as expenseService from '../../services/expenseService';
import { downloadBlob } from '../../utils/downloadFile';
import { BarChart, StackedBarChart, PieChart, LineChart, Heatmap, ExpenseList } from '../../components/ExpenseCharts';
import ExpenseTabs from '../../components/ExpenseTabs';
import { CollapsibleCard, BudgetVsActual } from '../../components/ExpenseDashboardWidgets';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const ADMIN_ROLES = ['System_Admin', 'CEO'];

const DATE_SHORTCUTS = [
  { value: '', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'ytd', label: 'YTD' },
];

// Up/down/flat delta vs. the immediately-preceding period of equal length
// (see _period_totals/_pct_change in expense_routes.py). null means no
// comparable prior period (e.g. an "all time" query) - shown as nothing,
// not a misleading 0%.
const TrendBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const up = pct > 0;
  const flat = pct === 0;
  const color = flat ? 'text-white/60' : up ? 'text-red-200' : 'text-green-200'; // up = spending more = warn
  const arrow = flat ? '→' : up ? '↑' : '↓';
  return <span className={`text-xs font-medium ${color} ml-1.5`}>{arrow} {Math.abs(pct)}%</span>;
};

const StatCard = ({ label, value, tone = 'gray', trendPct }) => {
  // 'accent' tile stands out with a brighter glass fill (matches the
  // "Total Expenses" tile in the glassmorphism mockup); the rest sit at a
  // quieter translucency so the accent tile still reads as the headline.
  const bg = tone === 'accent' ? 'bg-white/25' : 'bg-white/10';
  return (
    <div className={`rounded-2xl p-4 border border-white/25 ${bg}`}>
      <div className="text-2xl font-medium text-white flex items-baseline">
        {value}
        <TrendBadge pct={trendPct} />
      </div>
      <div className="text-xs text-white/75 mt-1">{label}</div>
    </div>
  );
};

const ExpenseDashboardPage = () => {
  const { user } = useAuthStore();
  const { projects, fetchProjects } = useProjectStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [budgetVsActual, setBudgetVsActual] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [projectFilter, setProjectFilter] = useState('');
  const [engineerFilter, setEngineerFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateShortcut, setDateShortcut] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const canSendEmail = user && ADMIN_ROLES.includes(user.role);

  const filterParams = useCallback(() => {
    const p = {};
    if (projectFilter) p.project_id = projectFilter;
    if (engineerFilter) p.engineer_id = engineerFilter;
    if (vendorFilter) p.vendor_id = vendorFilter;
    if (categoryFilter) p.category = categoryFilter;
    // A shortcut overrides a manual range, matching the backend's
    // dashboard_summary/charts precedence (date_shortcut wins if both are sent).
    if (dateShortcut) {
      p.date_shortcut = dateShortcut;
    } else {
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo) p.date_to = dateTo;
    }
    return p;
  }, [projectFilter, engineerFilter, vendorFilter, categoryFilter, dateShortcut, dateFrom, dateTo]);

  const clearFilters = () => {
    setProjectFilter('');
    setEngineerFilter('');
    setVendorFilter('');
    setCategoryFilter('');
    setDateShortcut('');
    setDateFrom('');
    setDateTo('');
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = filterParams();
      const [summaryRes, chartsRes] = await Promise.all([
        expenseService.getDashboardSummary(params),
        expenseService.getDashboardCharts(params),
      ]);
      setSummary(summaryRes.data.summary);
      setCharts(chartsRes.data.charts);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load dashboard', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);

  useEffect(() => { load(); }, [load]);

  // Actual-vs-budget follows the project filter only (budgets are lifetime
  // figures, so the date range doesn't apply): the selected project, or the
  // top 20 projects by cost when none is selected.
  useEffect(() => {
    const params = projectFilter ? { project_id: projectFilter } : { limit: 20 };
    expenseService.getDashboardBudgetVsActual(params)
      .then((res) => setBudgetVsActual(res.data.projects || []))
      .catch(() => showToast('Failed to load budget vs actual', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter]);

  useEffect(() => {
    // 'all' statuses: a completed/on-hold project still needs picking here
    // (same fix as the Purchases and Performance pages).
    fetchProjects('all');
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    expenseService.getVendors().then((res) => setVendors(res.data.vendors || [])).catch(() => {});
    // Alerts are a current-state snapshot (not scoped to the dashboard's
    // date/project filters), so they load once rather than re-fetching on
    // every filter change.
    expenseService.getDashboardAlerts().then((res) => setAlerts(res.data.alerts)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async (key, filename) => {
    try {
      const res = await expenseService.exportCsv(key);
      downloadBlob(res.data, filename);
    } catch (error) {
      showToast('Export failed', 'error');
    }
  };

  const handleSendWeeklySummary = async () => {
    if (!window.confirm('Send the weekly expense summary email now?')) return;
    try {
      await expenseService.sendWeeklySummaryNow();
      showToast('Weekly summary sent', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Email is not configured yet', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <ExpenseTabs />
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">Expense Dashboard</h1>
          {canSendEmail && (
            <button onClick={handleSendWeeklySummary} className="glass-widget text-xs px-3 py-1.5 text-white/90 hover:bg-white/10">
              Send Weekly Summary Now
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="glass-card p-4 mb-6 flex flex-wrap gap-2 items-center">
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={engineerFilter} onChange={(e) => setEngineerFilter(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All engineers</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All vendors</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All categories</option>
            {['Travel', 'Food', 'Accommodation', 'Materials', 'Transport', 'Other'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={dateShortcut} onChange={(e) => setDateShortcut(e.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            {DATE_SHORTCUTS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <span className="text-xs text-gray-400">or custom range:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); if (e.target.value) setDateShortcut(''); }}
            className="border border-gray-300 rounded px-2 py-1.5 text-xs"
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); if (e.target.value) setDateShortcut(''); }}
            className="border border-gray-300 rounded px-2 py-1.5 text-xs"
          />
          <button onClick={clearFilters} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 ml-auto">
            Clear All Filters
          </button>
        </div>

        {/* Summary */}
        {summary && (
          <div className="glass-widget p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <StatCard label="Total Expenses" value={money(summary.total_expenses)} tone="accent" trendPct={summary.trend?.pct_change?.total_expenses} />
              <StatCard label="Site Expenses" value={money(summary.site_expenses)} trendPct={summary.trend?.pct_change?.site_expenses} />
              <StatCard label="Factory Expenses" value={money(summary.factory_expenses)} trendPct={summary.trend?.pct_change?.factory_expenses} />
              <StatCard label="Vendor Expenses" value={money(summary.vendor_expenses)} trendPct={summary.trend?.pct_change?.vendor_expenses} />
              <StatCard label="Material Purchase" value={money(summary.material_expenses)} trendPct={summary.trend?.pct_change?.material_expenses} />
              {/* Labour (production-tracker daily reports) added 2026-09-29
                  per user request, so "Total Expenses" reflects true
                  all-in project cost. */}
              <StatCard label="Labour Cost" value={money(summary.labour_cost)} trendPct={summary.trend?.pct_change?.labour_cost} />
            </div>
            {summary.top_project && (
              <p className="text-xs text-white/75 mt-4">
                Top project: <span className="font-medium text-white">{summary.top_project}</span> ({money(summary.top_project_amount)})
              </p>
            )}
            {summary.trend && (
              <p className="text-xs text-white/50 mt-1">vs. previous period ({summary.trend.previous_period.date_from} to {summary.trend.previous_period.date_to})</p>
            )}
          </div>
        )}

        {/* Alerts - the "what needs my attention" panel a CEO glances at first */}
        {alerts && (alerts.pending_approvals_count > 0 || alerts.over_budget_projects.length > 0 || alerts.large_pending_vendor_payments.length > 0) && (
          <div className="glass-card p-6 mb-6 border-l-4 border-l-amber-400">
            <h2 className="font-semibold text-gray-900 mb-3">Needs attention</h2>
            <div className="space-y-1.5">
              {alerts.pending_approvals_count > 0 && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{alerts.pending_approvals_count}</span> expense{alerts.pending_approvals_count !== 1 ? 's' : ''} awaiting approval
                  {alerts.pending_approvals[0]?.days_pending > 0 && (
                    <span className="text-amber-700"> - oldest pending {alerts.pending_approvals[0].days_pending} day{alerts.pending_approvals[0].days_pending !== 1 ? 's' : ''}</span>
                  )}
                </p>
              )}
              {alerts.over_budget_projects.map((p) => (
                <p key={p.project_id} className="text-sm text-red-700">
                  <button onClick={() => navigate(`/expenses/site?project_id=${p.project_id}`)} className="font-medium hover:underline">{p.project_name}</button>
                  {' '}is {p.pct_used}% of budget ({money(p.actual)} of {money(p.budgeted)})
                </p>
              ))}
              {alerts.large_pending_vendor_payments.map((v) => (
                <p key={v.vendor_id} className="text-sm text-gray-700">
                  <button onClick={() => navigate(`/expenses/vendor?vendor_id=${v.vendor_id}`)} className="font-medium hover:underline">{v.vendor_name}</button>
                  {' '}is owed {money(v.amount_to_pay)}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Actual vs budget - the selected project, or the top 20 by cost.
            Open by default: it's the headline view; every other graph
            below starts minimised. */}
        <CollapsibleCard
          id="budget-vs-actual"
          title="Actual vs budget"
          subtitle={projectFilter ? 'selected project' : 'top 20 projects by cost'}
          defaultOpen
          className="mb-6"
        >
          <BudgetVsActual
            rows={budgetVsActual}
            onProjectClick={(r) => navigate(`/expenses/site?project_id=${r.project_id}`)}
          />
        </CollapsibleCard>

        {/* CSV exports */}
        <div className="glass-card p-4 mb-6 flex flex-wrap gap-2">
          <button onClick={() => handleExport('projects', 'expenses_by_project.csv')} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Export by Project</button>
          <button onClick={() => handleExport('engineers', 'expenses_by_engineer.csv')} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Export by Engineer</button>
          <button onClick={() => handleExport('vendors', 'expenses_by_vendor.csv')} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Export by Vendor</button>
          <button onClick={() => handleExport('all', 'all_expenses.csv')} className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Export All</button>
        </div>

        {isLoading ? (
          <p className="text-white/75 text-sm">Loading charts...</p>
        ) : charts ? (
          <div className="space-y-4">
            {/* Project cost is open by default (top 20 by cost); the rest
                start minimised and open on demand - state is remembered. */}
            <CollapsibleCard id="project-cost" title="Total project cost by project" subtitle="top 20 by cost" defaultOpen>
              <StackedBarChart
                data={charts.by_project}
                labelKey="project"
                segments={[
                  { key: 'site', label: 'Site expenses', color: '#2F6690' },
                  { key: 'vendor', label: 'Vendors (at site)', color: '#F59E0B' },
                  { key: 'material', label: 'Material purchase', color: '#10B981' },
                  { key: 'labour', label: 'Labour (production tracker)', color: '#EC4899' },
                ]}
                onBarClick={(row) => navigate(`/expenses/site?project_id=${row.project_id}`)}
              />
            </CollapsibleCard>
            <CollapsibleCard id="by-category" title="Expenses by category">
              <PieChart data={charts.by_category} labelKey="category" valueKey="amount" />
            </CollapsibleCard>
            <CollapsibleCard id="monthly-trend" title="Monthly trend">
              <LineChart
                data={charts.monthly_trend}
                xKey="month"
                series={[
                  { key: 'site', color: '#2F6690', label: 'Site' },
                  { key: 'factory', color: '#F59E0B', label: 'Factory' },
                  { key: 'vendor', color: '#10B981', label: 'Vendor' },
                  { key: 'material', color: '#8B5CF6', label: 'Material' },
                  { key: 'labour', color: '#EC4899', label: 'Labour' },
                ]}
              />
            </CollapsibleCard>
            <CollapsibleCard id="by-vendor" title="Expenses by vendor">
              <BarChart
                data={charts.by_vendor}
                labelKey="vendor"
                valueKey="amount"
                horizontal
                onBarClick={(row) => navigate(`/expenses/vendor?vendor_id=${row.vendor_id}`)}
              />
            </CollapsibleCard>
            <CollapsibleCard id="top-5" title="Top 5 expenses">
              <ExpenseList items={charts.top_5} />
            </CollapsibleCard>
            <CollapsibleCard id="engineer-category" title="Engineer x category">
              <Heatmap data={charts.heatmap} rowKey="engineer" colKey="category" valueKey="amount" />
            </CollapsibleCard>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ExpenseDashboardPage;
